import clsx from 'clsx'
import { type PropsWithChildren, useMemo } from 'react'
import { useLocation } from 'react-router'

import { FvLinkButton, type IconName } from '@fv/client-components'
import type { Workflow } from '@fv/models'

import { useWorkflowsWithStatus } from '@/features/profiile/hooks'
import { routes } from '@/routes'

type Props = {
  icon: IconName
  label: string
  path: string
  disabled?: boolean
}

type ModeNavProps = PropsWithChildren<{
  className?: string
}>

const modeNavConfig: Record<Workflow, { icon: IconName; label: string }> = {
  parcel: { icon: 'box', label: 'Parcel' },
  ltl: { icon: 'truck', label: 'LTL' },
  truckload: { icon: 'truck-moving', label: 'Truckload' },
}

const ModeLink = ({ icon, label, path, disabled }: Props) => {
  const { pathname } = useLocation()

  return (
    <FvLinkButton
      className={clsx(
        'border-fv-blue-300 b1350:px-4 b1350:py-2 b1350:mb-0 relative z-20 block rounded-lg border-2 p-4',
        {
          'after:border-fv-blue-300 b1350:after:right-1/2 b1350:after:translate-x-1/2  b1350:after:-rotate-45 b1350:after:bg-fv-blue-50 b1350:after:w-2 b1350:after:h-2 b1350:after:top-full bg-fv-beer-light after:absolute after:-right-3 after:top-2/4 after:z-10 after:h-3 after:w-3 after:-translate-y-1/2 after:translate-x-[calc(-50%_+_1px)] after:rotate-45 after:border-2 after:border-b-0 after:border-l-0 after:bg-fv-beer-light':
            !disabled && pathname.startsWith(path),
        },
        {
          'bg-white': disabled || !pathname.startsWith(path),
          'opacity-50': disabled,
        },
      )}
      disabled={disabled}
      icon={icon}
      to={disabled ? undefined : path}
    >
      {label}
    </FvLinkButton>
  )
}

export const ModeNav = ({ className, children }: ModeNavProps) => {
  const workflowsWithStatus = useWorkflowsWithStatus()
  const enabledByWorkflow = useMemo(
    () =>
      new Map(
        workflowsWithStatus.map(({ workflow, enabled }) => [workflow, enabled]),
      ),
    [workflowsWithStatus],
  )

  return (
    <nav
      className={clsx(
        className,
        'bg-fv-blue-50 overflow-auto border-r border-gray-300 py-8 px-6 flex flex-col gap-3  b1350:pt-5 b1350:pb-3 b1350:border-r-0 b1350:border-b b1350:col-span-full b1350:flex b1350:flex-row b1350:items-center',
      )}
    >
      {Object.entries(modeNavConfig).map(([workflowStr, { icon, label }]) => {
        const workflow = workflowStr as Workflow
        if (!enabledByWorkflow.has(workflow)) return null

        return (
          <ModeLink
            key={workflow}
            disabled={!enabledByWorkflow.get(workflow)}
            icon={icon}
            label={label}
            path={routes.quote(workflow)}
          />
        )
      })}
      {children}
    </nav>
  )
}
